import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import performRequest from "network/perform-request";
import { parseLeaguePaginatedResponse } from "components/pages/Gestion/gestion-pagination";

export function useGestionPaginatedList({ customerId, userId, user, route, onError }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const requestIdRef = useRef(0);
  const debouncedSearchRef = useRef(null);
  const onErrorRef = useRef(onError);
  const userRef = useRef(user);

  useEffect(() => {
    onErrorRef.current = onError;
    userRef.current = user;
  }, [onError, user]);

  const load = useCallback(
    ({ name = "", pageIndex = 0, perPage = 25, hard = false } = {}) => {
      if (!customerId || !userRef.current) return;

      const requestId = ++requestIdRef.current;

      if (hard) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }

      performRequest(
        "GET",
        route(customerId),
        {
          name: name || undefined,
          include_archived: true,
          page: pageIndex,
          page_size: perPage,
        },
        true,
        userRef.current,
      )
        .then((response) => {
          if (requestId !== requestIdRef.current) return;

          const parsed = parseLeaguePaginatedResponse(response.data);
          setItems(parsed.results);
          setPage(parsed.page);
          setTotalPages(parsed.totalPages);
          setTotalResults(parsed.totalResults);
          setPageSize((current) => parsed.pageSize || perPage || current);
        })
        .catch((error) => {
          if (requestId !== requestIdRef.current) return;
          onErrorRef.current?.(error);
          setItems([]);
          setTotalResults(0);
        })
        .finally(() => {
          if (requestId !== requestIdRef.current) return;
          setInitialLoading(false);
          setRefreshing(false);
        });
    },
    [customerId, route],
  );

  useEffect(() => {
    debouncedSearchRef.current = debounce((name, perPage) => {
      setPage(0);
      load({ name, pageIndex: 0, perPage });
    }, 500);

    return () => debouncedSearchRef.current?.cancel();
  }, [load]);

  useEffect(() => {
    requestIdRef.current += 1;
    setSearch("");
    setPage(0);
    setPageSize(25);
    setItems([]);
    setTotalResults(0);
    setInitialLoading(true);
    load({ name: "", pageIndex: 0, perPage: 25, hard: true });
  }, [customerId, userId, load]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);

    if (value === "") {
      debouncedSearchRef.current?.cancel();
      setPage(0);
      load({ name: "", pageIndex: 0, perPage: pageSize });
      return;
    }

    debouncedSearchRef.current?.(value, pageSize);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    load({ name: search, pageIndex: nextPage, perPage: pageSize });
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(0);
    load({ name: search, pageIndex: 0, perPage: nextPageSize });
  };

  const reload = () => load({ name: search, pageIndex: page, perPage: pageSize });

  return {
    items,
    search,
    page,
    pageSize,
    totalPages,
    totalResults,
    initialLoading,
    refreshing,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    reload,
  };
}
